/**
 * Coerce an API error payload into a human-readable string.
 *
 * Our API returns errors as `{ error: { code, message, details } }` (see
 * `handleApiError`). Client code that stores `error` directly into React state
 * and renders it will crash with React error #31 ("Objects are not valid as a
 * React child"). Always run the raw `error` value through this before display.
 *
 * Prefers the most specific message available: a Zod field message from
 * `details`, then the top-level `message`, then a supplied fallback.
 */
export function errorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'string' && err.trim()) return err

  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: unknown }

    // Zod validation details: an array of { message } issues — surface the first.
    if (Array.isArray(e.details) && e.details.length > 0) {
      const first = e.details[0] as { message?: unknown }
      if (first && typeof first.message === 'string' && first.message.trim()) {
        return first.message
      }
    }

    if (typeof e.message === 'string' && e.message.trim()) return e.message
  }

  return fallback
}
